/**
 * MorphAnimation — the signature interaction. Replaces the Task 059
 * ArrowMorph placeholder with two real choreographies:
 *
 *   - "bowstring":  light traces the composer outline -> arrow flies UP ->
 *                   sweep of light drops down -> thread opens (clip-path).
 *   - "aimed-shot": light gathers L->R along the composer's bottom edge ->
 *                   composer slightly tensions -> arrow streaks RIGHT ->
 *                   thread slides in from the right.
 *
 * The parent chooses one direction at submit time and passes it in. That
 * lets the Thread use the same direction for the scene-open animation after
 * the morph completes.
 *
 * Phase advancement uses a single sanctioned useEffect that schedules a
 * window.setTimeout matching the CSS animation duration. Adjacent comment
 * below names the external system, per §3 of the React guidelines. The
 * reducer is the source of truth — this Effect only translates "phase
 * ended" into a dispatch, mirroring the pattern the original Task 059
 * ArrowMorph established.
 *
 * Under prefers-reduced-motion the reducer skips both motion phases
 * entirely (see chat-reducer.ts "answer/received" case). If this component
 * is ever rendered with reducedMotion=true (defensive), it fires the
 * completion callback immediately so the state machine doesn't strand.
 *
 * Geometry: the parent passes the composer's DOMRect at submit time. We
 * convert it to CSS custom properties (--morph-w, --morph-dist, etc.) so
 * the keyframes can hit the right coordinates.
 */
import { useEffect, useRef, useMemo } from "react";
import type { CSSProperties } from "react";

export type MorphPhase = "drawing" | "landing" | "static";
export type MorphDirection = "bowstring" | "aimed-shot";

export interface MorphGeometry {
  /** Composer bounding box at submit time, in viewport coordinates. */
  composerRect: DOMRect;
}

export interface MorphAnimationProps {
  phase: MorphPhase;
  direction: MorphDirection;
  reducedMotion: boolean;
  geometry: MorphGeometry | null;
  onPhaseComplete: (completedPhase: Exclude<MorphPhase, "static">) => void;
}

// The landing flight duration — matches the CSS bs-fly / as-fly keyframes in
// styles/app.css. Both directions share it to keep the reducer timing
// direction-agnostic. The "drawing" phase has NO fixed duration: it is the
// loading state and holds until /answer resolves (Task 074).
const LANDING_DURATION_MS = 520;

export function MorphAnimation(props: MorphAnimationProps): JSX.Element {
  const { phase, direction, reducedMotion, geometry, onPhaseComplete } = props;

  // Keep the latest callback in a ref so the Effect's deps stay stable.
  const callbackRef = useRef(onPhaseComplete);
  callbackRef.current = onPhaseComplete;

  // external: window.setTimeout — schedules the landing-end signal that the
  // reducer needs to advance landing -> chat-active. The "drawing" phase has
  // NO timer (Task 074): it is the loading state and holds until the /answer
  // promise resolves and the reducer moves to "landing". A future
  // Motion-for-React refactor can swap this setTimeout for onAnimationComplete
  // without touching the reducer.
  useEffect(() => {
    if (phase === "static") return;
    if (phase === "drawing") {
      // Loading state: held by the network, not the clock. The reducer
      // advances on answer/received, not on a timer.
      return;
    }
    // phase === "landing": signal completion when the arrow's flight ends.
    if (reducedMotion) {
      // Defensive: the reducer skips landing under reduced motion, but if we
      // get here anyway, do not strand the machine.
      callbackRef.current("landing");
      return;
    }
    const handle = window.setTimeout(() => {
      callbackRef.current("landing");
    }, LANDING_DURATION_MS);
    return () => {
      window.clearTimeout(handle);
    };
  }, [phase, reducedMotion]);

  // Geometry derived during render. No useState, no useEffect.
  const overlayStyle = useMemo<CSSProperties>(() => {
    if (geometry === null) return {};
    const { composerRect } = geometry;
    const midY = composerRect.top + composerRect.height / 2;
    // Custom properties consumed by the CSS keyframes in app.css. Computing
    // them here keeps the choreography numerically synchronized with the
    // composer's actual position on the page.
    const cssVars: Record<string, string> = {
      "--morph-w": `${composerRect.width}px`,
      "--morph-top": `${Math.max(60, composerRect.top - 40)}px`,
      // Bowstring fly-distance: from composer top up off the viewport.
      "--morph-dist":
        direction === "bowstring"
          ? `-${composerRect.top + 60}px`
          : // Aimed Shot fly-distance: from composer right edge off the right
            // viewport edge plus a tail for the streaking trail.
            `${window.innerWidth - composerRect.right + 260}px`,
      "--morph-mid-y": `${midY}px`,
    };
    return cssVars as CSSProperties;
  }, [geometry, direction]);

  if (geometry === null || phase === "static") {
    return (
      <div
        className="morph"
        data-direction={direction}
        data-phase="static"
        data-testid="morph"
        aria-hidden="true"
      />
    );
  }

  const { composerRect } = geometry;
  const arrowPositionStyle: CSSProperties =
    direction === "bowstring"
      ? {
          left: composerRect.left + composerRect.width / 2 - 1.5,
          top: composerRect.top - 92,
        }
      : {
          left: composerRect.right - 8,
          top: composerRect.top + composerRect.height / 2 - 1.5,
        };

  return (
    <div
      className="morph"
      data-direction={direction}
      data-phase={phase}
      data-testid="morph"
      style={overlayStyle}
      aria-hidden="true"
    >
      {/* Bowstring: a "sweep" of light drops down on the resolving phase. */}
      {direction === "bowstring" ? (
        <>
          <svg
            className="trace"
            style={{
              left: composerRect.left,
              top: composerRect.top,
              width: composerRect.width,
              height: composerRect.height,
            }}
            viewBox={`0 0 ${composerRect.width} ${composerRect.height}`}
            aria-hidden="true"
          >
            <rect
              x="1"
              y="1"
              width={Math.max(0, composerRect.width - 2)}
              height={Math.max(0, composerRect.height - 2)}
              rx="22"
              pathLength="100"
            />
          </svg>
          <div
            className="morph__sweep"
            style={{ ["--morph-top" as string]: `${Math.max(60, composerRect.top - 40)}px` }}
          />
        </>
      ) : null}

      {/* Aimed Shot: a nock travels L->R and a tensioning bowstring line. */}
      {direction === "aimed-shot" ? (
        <>
          <div
            className="morph__string"
            style={{
              left: composerRect.left,
              top: composerRect.bottom - 2,
              width: composerRect.width,
            }}
          />
          <div
            className="morph__draw"
            style={{
              left: composerRect.left,
              top: composerRect.bottom - 7,
            }}
          />
        </>
      ) : null}

      {/* The arrow itself — shape + animation are direction-specific via CSS. */}
      <div className="morph__arrow" style={arrowPositionStyle} />
    </div>
  );
}
